import { FormEvent, ReactNode, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { inputCls } from '@/components/ui/form';
import { Spinner } from '@/components/ui/Spinner';
import { useIdentity } from './identity';

/** Full-screen gate: a spinner while auth resolves, the sign-in screen until someone signs in. */
export function SignInGate({ children }: { children: ReactNode }) {
  const { me, loading } = useIdentity();
  if (loading && !me) return <Splash />;
  if (!me) return <SignInScreen />;
  return <>{children}</>;
}

/** Brand chrome shared by the loading splash and the sign-in card. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-4 text-fg">
      <div className="w-full max-w-[380px]">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center">
            <img src="/newton-logo.png" alt="Newton" width={36} height={36} className="h-9 w-9 object-contain" />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-fg-strong">Spark Board</div>
            <div className="text-[12px] text-fg-faint">Sign in to your team backlog</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function Splash() {
  return (
    <Shell>
      <div className="flex items-center justify-center gap-2 rounded-card border border-line bg-surface p-6 text-fg-soft">
        <Spinner />
        <span className="text-[13px]">Signing you in…</span>
      </div>
    </Shell>
  );
}

function SignInScreen() {
  const { oidc } = useIdentity();
  return <Shell>{oidc ? <SsoSignIn /> : <EmailSignIn />}</Shell>;
}

/** Zitadel SSO: a single button that starts the redirect login. */
function SsoSignIn() {
  const { loginWithSSO } = useIdentity();
  const [busy, setBusy] = useState(false);
  return (
    <div className="rounded-card border border-line bg-surface p-5 shadow-xl shadow-black/30">
      <p className="mb-4 text-[13px] leading-relaxed text-fg-soft">
        Sign in with your Spark account to access the team backlog. Your account is created
        automatically the first time you sign in.
      </p>
      <Button
        type="button"
        variant="primary"
        className="w-full justify-center"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          loginWithSSO();
        }}
      >
        {busy ? <Spinner /> : 'Sign in with SSO'}
      </Button>
    </div>
  );
}

/** Legacy password-free email sign-in (used when OIDC is disabled). */
function EmailSignIn() {
  const { signIn } = useIdentity();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), name.trim() || undefined);
    } catch {
      setError('That email didn’t work. Check it and try again.');
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-card border border-line bg-surface p-5 shadow-xl shadow-black/30">
      <label className="mb-1 block text-[12px] font-medium text-fg-soft">Email</label>
      <input
        className={inputCls}
        type="email"
        autoFocus
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <label className="mb-1 mt-3 block text-[12px] font-medium text-fg-soft">
        Display name <span className="text-fg-ghost">(optional)</span>
      </label>
      <input
        className={inputCls}
        placeholder="How your name shows on the board"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      {error ? <div className="mt-3 text-[12px] text-danger">{error}</div> : null}
      <Button type="submit" variant="primary" className="mt-4 w-full justify-center" disabled={busy || !email.trim()}>
        {busy ? <Spinner /> : 'Continue'}
      </Button>
      <p className="mt-3 text-center text-[11px] text-fg-faint">
        No password needed — your email just identifies you to the team.
      </p>
    </form>
  );
}

export default SignInGate;
